import { Fragment, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { LuCircleHelp, LuFastForward, LuFlag, LuInfinity, LuLocateFixed, LuLock, LuMusic, LuPause, LuPlay, LuRotateCcw, LuRotateCw, LuSparkles, LuTrophy, LuVolume2, LuVolumeX, LuZap, LuZoomIn, LuZoomOut } from "react-icons/lu";
import { subjectName } from "../../shared/constants";
import {
  COLS, ROWS, TICKS_PER_SEC, TOWERS, TOWER_TYPES, WAVES, LAYER_COLORS, WaveSim, getMap, towerSellValue, waveBalloonCount,
  type TdMap, type Tower, type TowerDef, type TowerType, type TargetMode, type WaveEvInput,
} from "../../shared/games/taarn";
import { useCountdown } from "../components/QuestionCard";
import type { GameProps } from "../pages/Match";
import { CELL, H, W, drawBalloon, drawFx, drawScene, drawTower, ringFor, towerPortrait, type Fx } from "./taarnArt";
import type { Stage } from "./td3d/stage";
import { GameAudio } from "./td3d/audio";

const TARGET_LABEL: Record<TargetMode, string> = { first: "Først", last: "Sidst", strong: "Stærkest", close: "Tættest" };
const TOWER_KEYS = ["q", "w", "e", "r", "t"];
const POP_COLORS = ["#ff5d5d", "#5da8ff", "#5ddc6e", "#ffe066", "#ff8ad8"];
const ANS_COLORS = ["#2f7bff", "#ff8a1f", "#22b455", "#e0489b"];

/** Angle from a tower to the nearest piece of road, so idle defenders face the action. */
function makeIdleAim(map: TdMap) {
  const cache = new Map<string, number>();
  return (x: number, y: number) => {
    const k = `${x},${y}`;
    let a = cache.get(k);
    if (a === undefined) {
      let best = 1e9, bx = 0, by = 0;
      for (const [px, py] of map.cells) { const d = Math.hypot(px - x, py - y); if (d < best) { best = d; bx = px; by = py; } }
      a = Math.atan2(by - y, bx - x);
      cache.set(k, a);
    }
    return a;
  };
}

const fmt = (n: number, d = 1) => n.toFixed(d).replace(".", ",");
/** the numbers a child can use to compare defenders: what it costs and what it does at a given level */
function towerStats(def: TowerDef, lv: number): [string, string][] {
  const L = def.levels[lv - 1]!;
  const sec = (ticks: number) => `${fmt(ticks / TICKS_PER_SEC)} s`;
  if (L.income) return [["Indtægt", `+${L.income} guld`], ["Hvert", sec(L.cd)], ["Skyder", "Nej – den sparer op"]];
  if (L.buff) return [["Rækkevidde", `${fmt(L.range)} felter`], ["Bonus", `+${Math.round(L.buff * 100)}% skudtempo til naboer`], ["Skyder", "Nej – den hjælper de andre"]];
  const rows: [string, string][] = [["Rækkevidde", L.range >= 50 ? "Hele banen" : `${fmt(L.range)} felter`], ["Tempo", `${fmt(TICKS_PER_SEC / L.cd)} skud/s`]];
  if (L.dmg > 0) {
    let t = `${L.dmg} lag`;
    if (L.aura) t += ` · rammer ${L.multi ? `op til ${L.multi}` : "alle"}`;
    else {
      if (L.multi) t += ` · ${L.multi} mål`;
      if (L.pierce > 1 && !L.splash) t += ` · gennemborer ${L.pierce}`;
      if (L.splash) t += ` · sprængning ${fmt(L.splash)}`;
    }
    rows.push(["Skade", t]);
  }
  if (L.freeze > 0) rows.push(["Bremser", `${Math.round(L.slow * 100)}% fart i ${sec(L.freeze)}`]);
  if (L.knock) rows.push(["Skubber", `${L.knock} ${L.knock === 1 ? "felt" : "felter"} tilbage`]);
  if (L.burn) rows.push(["Brænder", sec(L.burn)]);
  if (L.critEvery) rows.push(["Kritisk", `hvert ${L.critEvery}. skud ×${L.critMul}`]);
  if (L.bossMul) rows.push(["Mod boss", `×${L.bossMul} skade`]);
  rows.push(["Bly", L.hitsLead ? "Kan ramme bly" : "Kan ikke ramme bly"]);
  return rows;
}
const GFX_ORDER = ["auto", "low", "medium", "high"] as const;
const GFX_LABEL = { auto: "automatisk", low: "lav", medium: "mellem", high: "høj" } as const;
const GFX_SHORT = { auto: "Auto", low: "Lav", medium: "Mel", high: "Høj" } as const;
interface Tip { type: TowerType; above: boolean; right: number; top: number; left: number; bottom: number }

export function Taarn(p: GameProps) {
  const st = p.view.state;
  const b = st.board;
  const over = b.phase === "over";
  const victory = b.phase === "victory";
  const endless = !!b.endless;
  const map = useMemo(() => getMap(b.mapId), [b.mapId]);
  const idleAim = useMemo(() => makeIdleAim(map), [map]);
  const mapRef = useRef(map);
  mapRef.current = map;
  const idleAimRef = useRef(idleAim);
  idleAimRef.current = idleAim;
  const reduced = typeof matchMedia !== "undefined" && matchMedia("(prefers-reduced-motion: reduce)").matches;

  const canvasRef = useRef<HTMLCanvasElement>(undefined as unknown as HTMLCanvasElement); // 2D fallback canvas
  const hostRef = useRef<HTMLDivElement>(undefined as unknown as HTMLDivElement); // holds the 3D canvas (or the 2D one)
  const stageRef = useRef<Stage | null>(null);
  const audioRef = useRef<GameAudio | null>(null);
  if (!audioRef.current) audioRef.current = new GameAudio();
  const audio = audioRef.current;
  const [tip, setTip] = useState<Tip | null>(null);
  // short "how to play" card over the stage: shown the first time (not for test robots), and with the ? button
  const [help, setHelp] = useState(false);
  useEffect(() => {
    try { if (!navigator.webdriver && !localStorage.getItem("td-help-v1")) setHelp(true); } catch { /* ignore */ }
  }, []);
  const closeHelp = useCallback(() => { setHelp(false); try { localStorage.setItem("td-help-v1", "1"); } catch { /* ignore */ } }, []);
  const [gfx, setGfx] = useState<"auto" | "low" | "medium" | "high">("auto");
  const [snd, setSnd] = useState(audio.soundOn);
  const [mus, setMus] = useState(audio.musicOn);
  const [mode, setMode] = useState<"loading" | "3d" | "2d">("loading");
  const modeRef = useRef(mode);
  modeRef.current = mode;
  const pRef = useRef(p);
  pRef.current = p;
  const boardRef = useRef(b);
  boardRef.current = b;
  const simRef = useRef<WaveSim | null>(null);
  const fxRef = useRef<Fx[]>([]);
  const aimRef = useRef(new Map<number, number>());
  const recoilRef = useRef(new Map<number, number>());
  const finishingRef = useRef(false);
  const [, setRenderTick] = useState(0);
  const bump = useCallback(() => setRenderTick((n: number) => n + 1), []);
  const [placing, setPlacing] = useState<TowerType | null>(null);
  const [selId, setSelId] = useState<number | null>(null);
  const [hover, setHover] = useState<[number, number] | null>(null);
  const [cursor, setCursor] = useState<[number, number]>([3, 2]);
  const [speed, setSpeed] = useState(1);
  const [paused, setPaused] = useState(false);
  const [auto, setAuto] = useState(false);
  const [msg, setMsg] = useState("");
  const [answering, setAnswering] = useState(false);
  const [banner, setBanner] = useState<any>(null); // quiz feedback shown for a moment
  const [roundNote, setRoundNote] = useState<string>("");
  const [resync, setResync] = useState(0);
  const [visible, setVisible] = useState(true);
  const stateRef = useRef({ placing, selId, hover, cursor });
  stateRef.current = { placing, selId, hover, cursor };
  const speedRef = useRef(1);
  const turboRef = useRef(1); // test automation only (navigator.webdriver): lets a slow software renderer finish a round
  speedRef.current = speed;
  const pausedRef = useRef(false);
  pausedRef.current = paused;
  const autoRef = useRef(false);
  autoRef.current = auto;

  /* ───────────── sound: the AudioContext may only start after a user gesture ───────────── */
  useEffect(() => {
    const a = audioRef.current!;
    const unlock = () => a.unlock();
    window.addEventListener("pointerdown", unlock, true);
    window.addEventListener("keydown", unlock, true);
    a.playMusic(true);
    return () => {
      window.removeEventListener("pointerdown", unlock, true);
      window.removeEventListener("keydown", unlock, true);
      a.dispose();
    };
  }, []);

  /* ───────────── request queue (keeps the order of ticks) ───────────── */
  const qRef = useRef<Promise<unknown>>(Promise.resolve());
  const enqueue = useCallback(<T,>(fn: () => Promise<T>): Promise<T> => {
    const r = qRef.current.then(fn, fn);
    qRef.current = r.catch(() => undefined);
    return r;
  }, []);

  const say = useCallback((t: string) => {
    audioRef.current?.deny();
    setMsg(t);
    window.setTimeout(() => setMsg((m) => (m === t ? "" : m)), 3200);
  }, []);
  // little "+35" bubbles that rise out of the gold counter
  const [startMsg, setStartMsg] = useState("");
  const [gains, setGains] = useState<{ id: number; text: string; kind: string }[]>([]);
  const gainId = useRef(0);
  const pushGain = useCallback((text: string, kind: string) => {
    const id = ++gainId.current;
    setGains((g) => [...g.slice(-3), { id, text, kind }]);
    window.setTimeout(() => setGains((g) => g.filter((x) => x.id !== id)), 1500);
  }, []);

  const sim = simRef.current;
  const live = !!sim;
  const towers: Tower[] = sim ? sim.towers : b.towers;
  const cash: number = sim ? sim.cash : b.cash;
  const lives: number = sim ? Math.max(0, b.lives - sim.livesLost) : b.lives;
  const boosting = !!sim && sim.boosting;
  const sel: Tower | null = towers.find((t) => t.id === selId) ?? null;
  const waveNo = endless ? b.wave + 1 : Math.min(b.wave + 1, b.waves);
  const planned = victory ? 0 : waveBalloonCount(b.wave, b.difficulty, map);
  const lvlNow: number = Number((b as any).playerLevel) || 1;
  const lockedMsg = (type: TowerType) => `${TOWERS[type].name} åbner på niveau ${TOWERS[type].unlock}. Spil flere spil og stig i niveau for at låse den op!`;
  const openCount = TOWER_TYPES.filter((k) => TOWERS[k].unlock <= lvlNow).length;
  const nextUnlock = TOWER_TYPES.map((k) => TOWERS[k].unlock).filter((u) => u > lvlNow).sort((a, c) => a - c)[0] ?? null;
  // the shop pictures come out of the 3D models: draw them one by one so a long list never freezes the page
  const [picsReady, setPicsReady] = useState(5);
  useEffect(() => {
    if (mode === "2d") { setPicsReady(99); return; }
    if (mode !== "3d" || picsReady >= openCount) return;
    const id = window.setTimeout(() => setPicsReady((n) => n + 1), 80);
    return () => window.clearTimeout(id);
  }, [mode, picsReady, openCount]);

  /* ───────────── live round: start, replay after reload, finish ───────────── */
  const rebuild = useCallback(() => {
    const run = boardRef.current.run;
    if (!run) return;
    const s = new WaveSim(run.towers, run.index, { cash: run.cash, nextId: run.nextId, lives: boardRef.current.lives, map: mapRef.current, difficulty: boardRef.current.difficulty });
    const at = simRef.current?.tick ?? (run.events.length ? run.events[run.events.length - 1].t : 0);
    s.runTo(run.events, at);
    simRef.current = s;
    stageRef.current?.reset();
    bump();
  }, []);

  // page (re)loaded in the middle of a round → rebuild it from the events the server has recorded
  useEffect(() => {
    if (b.run && !simRef.current && !finishingRef.current) {
      rebuild();
      setPaused(false);
    }
  }, [b.run?.index, rebuild]);
  // a failed live action: take the server's version of events and continue from the current tick
  useEffect(() => {
    if (resync > 0) rebuild();
  }, [resync, rebuild]);

  const startRound = useCallback(() => {
    const bb = boardRef.current;
    if (simRef.current || bb.phase !== "build") return;
    if (bb.towers.length === 0) return say("Køb mindst ét forsvar, før du starter runden.");
    setSelId(null);
    setPlacing(null);
    setRoundNote("");
    simRef.current = new WaveSim(bb.towers, bb.wave, { cash: bb.cash, nextId: bb.nextId, lives: bb.lives, map: mapRef.current, difficulty: bb.difficulty });
    stageRef.current?.reset();
    audioRef.current?.waveStart();
    const nb = simRef.current.planned;
    setStartMsg(`Runde ${bb.wave + 1}: ${nb} balloner på vej!`);
    window.setTimeout(() => setStartMsg(""), 2600);
    finishingRef.current = false;
    setPaused(false);
    bump();
    void enqueue(async () => {
      const ok = await pRef.current.act({ type: "startRun" });
      if (!ok) {
        simRef.current = null;
        bump();
      }
    });
  }, [enqueue, say]);

  const finishRound = useCallback(() => {
    if (finishingRef.current) return;
    finishingRef.current = true;
    void enqueue(async () => {
      await pRef.current.act({ type: "finishWave" });
      finishingRef.current = false;
      simRef.current = null;
      fxRef.current = [];
      bump();
    });
  }, [enqueue]);

  // announce the result of a finished round (and optionally start the next one)
  const lastIdx = useRef<number>(b.lastWave ? b.lastWave.index : -1);
  useEffect(() => {
    const lw = b.lastWave;
    if (!lw || lw.index === lastIdx.current) return;
    lastIdx.current = lw.index;
    audioRef.current?.correct();
    setRoundNote(`Runde ${lw.index + 1} klaret! ${lw.pops} lag punkteret · +${lw.cash + lw.bonus} guld`);
    if (autoRef.current && !over && b.phase === "build" && b.towers.length) {
      const id = window.setTimeout(() => startRound(), 1800);
      return () => window.clearTimeout(id);
    }
  }, [b.lastWave?.index]);

  const wasOver = useRef(over);
  useEffect(() => {
    if (over && !wasOver.current) { if (st.won) audioRef.current?.win(); else audioRef.current?.lose(); }
    wasOver.current = over;
  }, [over]);
  // round 15 cleared: a fanfare, and the victory card asks whether to keep going
  const wasVictory = useRef(victory);
  useEffect(() => {
    if (victory && !wasVictory.current) { audioRef.current?.win(); setSelId(null); setPlacing(null); }
    wasVictory.current = victory;
  }, [victory]);
  const [confirmEnd, setConfirmEnd] = useState(false);
  useEffect(() => { if (!endless || live) setConfirmEnd(false); }, [endless, live]);
  const choose = useCallback((what: "continue" | "finish") => {
    audioRef.current?.click();
    void enqueue(async () => { await pRef.current.act({ type: what }); });
  }, [enqueue]);
  // the music gets busier while a wave is running
  useEffect(() => { audioRef.current?.setIntensity(live ? 1 : 0); }, [live]);

  /* ───────────── actions during/between rounds ───────────── */
  const doAct = useCallback((a: { type: "build" | "upgrade" | "sell" | "target" | "boost"; [k: string]: unknown }) => {
    const s = simRef.current;
    const chime = () => { const au = audioRef.current; if (!au) return; if (a.type === "build") au.buy(); else if (a.type === "upgrade") au.upgrade(); else if (a.type === "sell") au.sell(); else if (a.type === "boost") au.upgrade(); else au.click(); };
    if (!s) {
      chime();
      void enqueue(() => pRef.current.act(a));
      return true;
    }
    if (s.done()) return say("Runden er slut."), false;
    if (a.type === "boost" && (boardRef.current.charges ?? 0) <= 0) return say("Svar rigtigt tre gange i træk for at lade Hjerne-boost op."), false;
    const { type, ...rest } = a;
    try {
      s.apply({ k: type, ...rest } as WaveEvInput);
    } catch (e) {
      say(e instanceof Error ? e.message : "Det kan du ikke lige nu.");
      return false;
    }
    const tick = s.tick;
    chime();
    bump();
    void enqueue(async () => {
      const ok = await pRef.current.act({ ...a, tick });
      if (!ok) setResync((n) => n + 1);
    });
    return true;
  }, [enqueue, say]);

  const placeAt = (x: number, y: number) => {
    if (!placing || over) return;
    const def = TOWERS[placing];
    if (def.unlock > lvlNow) return say(lockedMsg(placing));
    if (map.isPath(x, y)) return say("Du kan ikke bygge på vejen.");
    if (towers.some((t) => t.x === x && t.y === y)) return say("Der står allerede et forsvar her.");
    if (towers.length >= b.maxTowers) return say(`Du kan højst have ${b.maxTowers} forsvarere.`);
    if (cash < def.cost) return say(`Du mangler guld – ${def.name} koster ${def.cost}. Svar på quizzen for at få mere!`);
    if (doAct({ type: "build", x, y, tower: placing })) setPlacing(null);
  };

  const interact = (x: number, y: number) => {
    const t = towers.find((q) => q.x === x && q.y === y);
    if (t) { setSelId(t.id); setPlacing(null); return; }
    if (placing) return placeAt(x, y);
    setSelId(null);
  };

  /* ───────────── quiz flow ───────────── */
  const q = st.question;
  const showBanner = useCallback((fb: any) => {
    setBanner(fb);
    window.setTimeout(() => setBanner((cur: any) => (cur === fb ? null : cur)), fb.correct ? 1300 : 3800);
  }, []);
  // answer reward arrives → show it, and during a live round credit the same gold to the local simulation
  const seenFb = useRef<any>(null);
  useEffect(() => {
    const fb: any = p.feedback;
    if (!fb || fb === seenFb.current) return;
    seenFb.current = fb;
    if (fb.correct) audioRef.current?.correct(); else audioRef.current?.wrong();
    showBanner(fb);
    p.clearFeedback();
    const g = fb.reward?.gold ?? 0;
    if (g > 0 && simRef.current && !simRef.current.done()) {
      try { simRef.current.apply({ k: "cash", amount: g }); } catch { /* ignore */ }
      bump();
    }
    if (g > 0) pushGain(`+${g}`, "gold");
    if (fb.reward?.charge) pushGain("⚡ Boost klar!", "boost");
  }, [p.feedback]);
  // a question that ran out of time (the server recorded it)
  const seenTimeout = useRef<string>("");
  useEffect(() => {
    const f: any = st.feedback;
    if (f?.timedOut && !q && f.pid !== seenTimeout.current) {
      seenTimeout.current = f.pid;
      showBanner(f);
    }
  }, [st.feedback, q]);
  useEffect(() => {
    const h = () => setVisible(document.visibilityState === "visible");
    document.addEventListener("visibilitychange", h);
    return () => document.removeEventListener("visibilitychange", h);
  }, []);
  // always keep one question open (only while the tab is visible)
  const askingRef = useRef(false);
  useEffect(() => {
    if (over || victory || q || banner || !visible || askingRef.current) return;
    const id = window.setTimeout(() => {
      if (askingRef.current) return;
      askingRef.current = true;
      void pRef.current.act({ type: "ask" }).finally(() => { askingRef.current = false; });
    }, 250);
    return () => window.clearTimeout(id);
  }, [over, victory, !!q, !!banner, visible, q?.pid]);

  const submitAnswer = useCallback((given: number | string) => {
    if (answering || !pRef.current.view.state.question) return;
    setAnswering(true);
    const tick = simRef.current?.tick ?? 0;
    void enqueue(() => pRef.current.answer(given, { tick })).finally(() => setAnswering(false));
  }, [answering, enqueue]);

  /* ───────────── 3D stage: loaded on demand; the flat 2D view is the fallback when WebGL is missing ───────────── */
  useEffect(() => {
    let dead = false;
    const force2d = (() => { try { return new URLSearchParams(location.search).get("td") === "2d" || localStorage.getItem("td-render") === "2d"; } catch { return false; } })();
    if (force2d) { setMode("2d"); return; }
    import("./td3d/stage").then(({ Stage: S }) => {
      if (dead) return;
      const st = S.create(hostRef.current, mapRef.current.id);
      if (!st) { setMode("2d"); return; }
      st.onContextLost = () => { st.dispose(); if (stageRef.current === st) stageRef.current = null; setMode("2d"); };
      st.setSfx(audioRef.current!);
      let hintAt = 0;
      st.rig.onWheelHint = () => { const t = Date.now(); if (t - hintAt > 20000) { hintAt = t; say("Hold Ctrl nede og brug musehjulet for at zoome – ellers ruller siden."); } };
      st.rig.calm = reduced; // reduced motion: no camera shake – but the round is always played out on the field
      stageRef.current = st;
      setMode("3d");
    }).catch((e) => { console.warn("3D kunne ikke indlæses", e); if (!dead) setMode("2d"); });
    return () => { dead = true; stageRef.current?.dispose(); stageRef.current = null; };
  }, []);

  /* ───────────── main loop: advance the simulation, then draw (3D stage, or the 2D fallback) ───────────── */
  useEffect(() => {
    if (mode === "loading") return;
    const stage = mode === "3d" ? stageRef.current : null;
    const cv = mode === "2d" ? canvasRef.current : null;
    const ctx = cv ? cv.getContext("2d") : null;
    if (cv && ctx) {
      const dpr = Math.min(2, window.devicePixelRatio || 1);
      cv.width = W * dpr;
      cv.height = H * dpr;
      ctx.scale(dpr, dpr);
    }
    if (mode === "3d" && !stage) return;
    let raf = 0;
    let last = performance.now();
    let acc = 0;
    let hudAt = 0;
    const TICK = 1000 / TICKS_PER_SEC;
    const prev = new Map<number, number>();
    const frame = (now: number) => {
      const tb = turboRef.current;
      const dt = Math.min(100 * tb, now - last);
      last = now;
      const s = simRef.current;
      if (s && !pausedRef.current && !s.done()) {
        acc += dt * speedRef.current;
        let guard = 0;
        while (acc >= TICK && !s.done() && guard++ < 14 * tb) {
          acc -= TICK;
          for (const bl of s.balloons) prev.set(bl.id, bl.d);
          const r = s.step();
          if (stage) { stage.onStep(s, r, now, speedRef.current); continue; }
          for (const sh of r.shots) {
            const tw = s.towers.find((t) => t.id === sh.tid);
            if (!tw) continue;
            const tx = (tw.x + 0.5) * CELL, ty = (tw.y + 0.5) * CELL, ex = sh.x * CELL, ey = sh.y * CELL;
            aimRef.current.set(tw.id, Math.atan2(ey - ty, ex - tx));
            recoilRef.current.set(tw.id, now);
            audioRef.current?.shot(tw.type, tw.level);
            const ax = tx + Math.cos(aimRef.current.get(tw.id)!) * 26, ay = ty + Math.sin(aimRef.current.get(tw.id)!) * 26;
            switch (tw.type) {
              case "pil": fxRef.current.push({ kind: "arrow", x: ax, y: ay, x2: ex, y2: ey, born: now, life: 150, color: "#fff" }); break;
              case "kanon": fxRef.current.push({ kind: "flash", x: ax, y: ay, born: now, life: 140, color: "#fff" }, { kind: "ball", x: ax, y: ay, x2: ex, y2: ey, born: now, life: 300, color: "#222" }, { kind: "boom", x: ex, y: ey, born: now + 300, life: 320, color: "#fa0", big: tw.level >= 2 }); break;
              case "is": fxRef.current.push({ kind: "nova", x: tx, y: ty, born: now, life: 420, color: "#7fdcff", big: tw.level >= 3 }); break;
              case "snigskytte": fxRef.current.push({ kind: "flash", x: ax, y: ay, born: now, life: 120, color: "#fff" }, { kind: "snipe", x: ax, y: ay, x2: ex, y2: ey, born: now, life: 170, color: "#b48cff" }); break;
              case "trold": fxRef.current.push({ kind: "orb", x: ax, y: ay, x2: ex, y2: ey, born: now, life: 240, color: "#ff7ab8" }); break;
              default: { // the newer defenders in the flat fallback view: area shots pulse, the rest fly as coloured orbs, big blasts explode
                const d = TOWERS[tw.type], l = d.levels[tw.level - 1]!;
                if (l.aura) fxRef.current.push({ kind: "nova", x: tx, y: ty, born: now, life: 420, color: d.color, big: tw.level >= 3 });
                else {
                  fxRef.current.push({ kind: "orb", x: ax, y: ay, x2: ex, y2: ey, born: now, life: 260, color: d.color });
                  if (l.splash >= 1) fxRef.current.push({ kind: "boom", x: ex, y: ey, born: now + 260, life: 320, color: "#fa0", big: l.splash >= 1.5 });
                }
              }
            }
          }
          for (const inc of r.income) {
            const tw = s.towers.find((t) => t.id === inc.tid);
            if (tw) { fxRef.current.push({ kind: "text", x: (tw.x + 0.5) * CELL, y: tw.y * CELL, text: `+${inc.amount}`, color: "#ffe066", born: now, life: 800 }); audioRef.current?.coin(); }
          }
          for (const pp of r.pops) audioRef.current?.pop(1 + (pp.layers % 4) * 0.08, pp.boss);
          for (const pp of r.pops) fxRef.current.push({ kind: "pop", x: pp.x * CELL, y: pp.y * CELL, born: now, life: 280, color: POP_COLORS[(pp.layers + Math.floor(pp.x * 7)) % POP_COLORS.length]!, big: pp.boss });
          if (r.gold > 0 && r.pops.length) fxRef.current.push({ kind: "text", x: r.pops[0]!.x * CELL, y: r.pops[0]!.y * CELL - 10, text: `+${r.gold}`, color: "#ffe066", born: now, life: 650 });
        }
        if (s.done() && !finishingRef.current) {
          bump();
          finishRound();
        }
      }
      if (now - hudAt > 120) {
        hudAt = now;
        if (simRef.current) bump();
      }
      const { placing: pl, selId: sid, hover: hv, cursor: cur } = stateRef.current;
      const ss = simRef.current;
      const tw = ss ? ss.towers : boardRef.current.towers;
      const boost = !!ss && ss.boosting;
      if (stage) {
        const ok = !!hv && !mapRef.current.isPath(hv[0], hv[1]) && !tw.some((t: Tower) => t.x === hv[0] && t.y === hv[1]) && tw.length < boardRef.current.maxTowers;
        stage.render({
          towers: tw, sim: ss, alpha: !ss || pausedRef.current ? 1 : Math.min(1, acc / TICK), selId: sid, placing: pl, hover: hv, hoverOk: ok,
          cursor: document.activeElement === hostRef.current ? cur : null, boost, paused: pausedRef.current, speed: speedRef.current,
        }, now);
        raf = requestAnimationFrame(frame);
        return;
      }
      if (!ctx || !cv) { raf = requestAnimationFrame(frame); return; }
      // ── 2D fallback drawing
      ctx.save();
      ctx.clearRect(0, 0, W, H);
      drawScene(ctx, mapRef.current);
      const selT = tw.find((t: Tower) => t.id === sid);
      if (selT) ringFor(ctx, selT.x, selT.y, TOWERS[selT.type as TowerType].levels[selT.level - 1]!.range);
      for (const t of [...tw].sort((a: Tower, c: Tower) => a.y - c.y)) {
        const aim = aimRef.current.get(t.id) ?? idleAimRef.current(t.x, t.y);
        const rb = recoilRef.current.get(t.id);
        const recoil = rb ? Math.max(0, 1 - (now - rb) / 150) : 0;
        drawTower(ctx, t as Tower, aim, { boost, recoil, time: reduced ? 0 : now / 1000 + t.id });
      }
      if (selT) {
        ctx.strokeStyle = "#ffe066"; ctx.lineWidth = 3; ctx.setLineDash([8, 5]);
        ctx.beginPath(); ctx.arc(selT.x * CELL + CELL / 2, selT.y * CELL + CELL / 2 + 2, 31, 0, 7); ctx.stroke(); ctx.setLineDash([]);
      }
      if (ss) {
        const alpha = pausedRef.current ? 1 : Math.min(1, acc / TICK);
        const sorted = [...ss.balloons].sort((a, c) => a.d - c.d);
        for (const bl of sorted) {
          const pd = prev.get(bl.id) ?? bl.d;
          drawBalloon(ctx, bl, pd + (bl.d - pd) * alpha, ss.tick, ss.tick < bl.slowUntil, mapRef.current);
        }
      }
      fxRef.current = fxRef.current.filter((f) => now - f.born < f.life);
      for (const f of fxRef.current) if (now >= f.born) drawFx(ctx, f, now);
      if (pl && hv) {
        const ok = !mapRef.current.isPath(hv[0], hv[1]) && !tw.some((t: Tower) => t.x === hv[0] && t.y === hv[1]);
        ringFor(ctx, hv[0], hv[1], TOWERS[pl].levels[0].range, ok);
        drawTower(ctx, { type: pl, level: 1, x: hv[0], y: hv[1] }, idleAimRef.current(hv[0], hv[1]), { ghost: true, time: now / 1000 });
        if (!ok) { ctx.fillStyle = "rgba(255,60,60,.35)"; ctx.fillRect(hv[0] * CELL, hv[1] * CELL, CELL, CELL); }
      }
      if (boost) {
        const g = ctx.createRadialGradient(W / 2, H / 2, H * 0.4, W / 2, H / 2, W * 0.7);
        g.addColorStop(0, "rgba(255,220,60,0)"); g.addColorStop(1, "rgba(255,200,40,.32)");
        ctx.fillStyle = g; ctx.fillRect(0, 0, W, H);
      }
      if (document.activeElement === hostRef.current) {
        ctx.strokeStyle = "#fff"; ctx.setLineDash([8, 6]); ctx.lineWidth = 3;
        ctx.strokeRect(cur[0] * CELL + 3, cur[1] * CELL + 3, CELL - 6, CELL - 6); ctx.setLineDash([]);
      }
      ctx.restore();
      raf = requestAnimationFrame(frame);
    };
    raf = requestAnimationFrame(frame);
    return () => cancelAnimationFrame(raf);
  }, [mode]);

  // automation hook (only when the browser is driven by a test tool)
  useEffect(() => {
    if (typeof navigator === "undefined" || !navigator.webdriver) return;
    (window as any).__td = {
      mode: () => modeRef.current,
      cellToClient: (x: number, y: number) => {
        const st = stageRef.current;
        if (st) return st.cellToClient(x, y);
        const r = (canvasRef.current ?? hostRef.current).getBoundingClientRect();
        return { x: r.left + ((x + 0.5) / COLS) * r.width, y: r.top + ((y + 0.5) / ROWS) * r.height };
      },
      stage: () => stageRef.current,
      turbo: (n: number) => { turboRef.current = Math.max(1, Math.min(40, n)); },
    };
    return () => { delete (window as any).__td; };
  }, []);

  /* ───────────── input ───────────── */
  const cellFrom = (e: { clientX: number; clientY: number }): [number, number] | null => {
    const st = stageRef.current;
    if (st && modeRef.current === "3d") return st.pickCell(e.clientX, e.clientY, simRef.current ? simRef.current.towers : boardRef.current.towers);
    const r = (canvasRef.current ?? hostRef.current).getBoundingClientRect();
    const x = Math.floor(((e.clientX - r.left) / r.width) * COLS);
    const y = Math.floor(((e.clientY - r.top) / r.height) * ROWS);
    return [Math.max(0, Math.min(COLS - 1, x)), Math.max(0, Math.min(ROWS - 1, y))];
  };
  useEffect(() => {
    if (mode !== "3d") return;
    try { const v = localStorage.getItem("td-quality"); setGfx(v === "low" || v === "medium" || v === "high" ? v : "auto"); } catch { /* ignore */ }
  }, [mode]);
  const cycleGfx = () => {
    const nx = GFX_ORDER[(GFX_ORDER.indexOf(gfx) + 1) % GFX_ORDER.length]!;
    setGfx(nx);
    const st = stageRef.current;
    if (!st) return;
    if (nx === "auto") st.setAuto(); else st.setQuality(nx, true);
  };
  const showTip = (type: TowerType, el: HTMLElement) => {
    const r = el.getBoundingClientRect();
    const vw = window.innerWidth, vh = window.innerHeight;
    const side = (el.closest(".td2-side") ?? el).getBoundingClientRect();
    if (vw >= 1021 && side.left > 300) {
      // wide layout: the bubble sits to the left of the whole shop, so it never hides the other cards
      setTip({ type, above: false, right: vw - side.left + 12, top: Math.min(vh - 112, Math.max(176, r.top + r.height / 2)), left: 0, bottom: 0 });
    } else {
      // narrow layout (the shop is under the stage): above the card
      setTip({ type, above: true, right: 0, top: 0, left: Math.min(vw - 136, Math.max(136, r.left + r.width / 2)), bottom: vh - r.top + 10 });
    }
  };
  const pick = (type: TowerType) => {
    if (over) return;
    if (TOWERS[type].unlock > lvlNow) { say(lockedMsg(type)); return; }
    audioRef.current?.click();
    setSelId(null);
    setPlacing((cur) => (cur === type ? null : type));
  };
  const dragRef = useRef<{ type: TowerType; x: number; y: number } | null>(null);
  const onCardDown = (type: TowerType, e: any) => {
    dragRef.current = { type, x: e.clientX, y: e.clientY };
  };
  useEffect(() => {
    const move = (e: PointerEvent) => {
      const d = dragRef.current;
      if (!d || Math.hypot(e.clientX - d.x, e.clientY - d.y) < 10) return;
      setPlacing(d.type);
      setSelId(null);
      const r = hostRef.current.getBoundingClientRect();
      if (e.clientX >= r.left && e.clientX <= r.right && e.clientY >= r.top && e.clientY <= r.bottom) setHover(cellFrom(e));
    };
    const up = (e: PointerEvent) => {
      const d = dragRef.current;
      dragRef.current = null;
      if (!d || Math.hypot(e.clientX - d.x, e.clientY - d.y) < 10) return;
      const r = hostRef.current.getBoundingClientRect();
      if (e.clientX >= r.left && e.clientX <= r.right && e.clientY >= r.top && e.clientY <= r.bottom) {
        const c = cellFrom(e);
        if (c) window.dispatchEvent(new CustomEvent("td-drop", { detail: { x: c[0], y: c[1], type: d.type } }));
      }
    };
    window.addEventListener("pointermove", move);
    window.addEventListener("pointerup", up);
    return () => { window.removeEventListener("pointermove", move); window.removeEventListener("pointerup", up); };
  }, []);
  useEffect(() => {
    const h = (e: Event) => {
      const d = (e as CustomEvent).detail as { x: number; y: number; type: TowerType };
      const def = TOWERS[d.type];
      if (def.unlock > lvlNow) return say(lockedMsg(d.type));
      setPlacing(d.type);
      if (mapRef.current.isPath(d.x, d.y)) return say("Du kan ikke bygge på vejen.");
      const tws = simRef.current ? simRef.current.towers : boardRef.current.towers;
      if (tws.some((t: Tower) => t.x === d.x && t.y === d.y)) return say("Der står allerede et forsvar her.");
      const c = simRef.current ? simRef.current.cash : boardRef.current.cash;
      if (c < def.cost) return say(`Du mangler guld – ${def.name} koster ${def.cost}.`);
      if (doAct({ type: "build", x: d.x, y: d.y, tower: d.type })) setPlacing(null);
    };
    window.addEventListener("td-drop", h);
    return () => window.removeEventListener("td-drop", h);
  }, [doAct, say, lvlNow]);

  useEffect(() => {
    const h = (e: KeyboardEvent) => {
      if ((e.target as HTMLElement)?.matches?.("input,textarea,select")) return;
      const k = e.key.toLowerCase();
      const ti = TOWER_KEYS.indexOf(k);
      const n = Number(e.key);
      const qq = pRef.current.view.state.question;
      if (qq && qq.kind !== "short" && n >= 1 && n <= qq.options.length) { e.preventDefault(); submitAnswer(n - 1); }
      else if (ti >= 0 && !e.ctrlKey && !e.metaKey) pick(TOWER_TYPES[ti]!);
      else if (e.key === "Escape") { if (help) closeHelp(); setPlacing(null); setSelId(null); }
      else if (k === "f") setSpeed((s) => (s % 3) + 1);
      else if (k === "b") doAct({ type: "boost" });
      else if (e.key === " " && !(e.target as HTMLElement)?.matches?.("button,a")) {
        e.preventDefault();
        if (simRef.current) setPaused((v) => !v); else startRound();
      } else if ((e.key === "Delete" || e.key === "Backspace") && selId !== null) doAct({ type: "sell", id: selId });
    };
    document.addEventListener("keydown", h);
    return () => document.removeEventListener("keydown", h);
  });
  const onCanvasKey = (e: any) => {
    const m: Record<string, [number, number]> = { ArrowUp: [0, -1], ArrowDown: [0, 1], ArrowLeft: [-1, 0], ArrowRight: [1, 0] };
    const d = m[e.key];
    if (d) {
      e.preventDefault();
      const n: [number, number] = [Math.max(0, Math.min(COLS - 1, cursor[0] + d[0])), Math.max(0, Math.min(ROWS - 1, cursor[1] + d[1]))];
      setCursor(n);
      setHover(n);
    } else if (e.key === "Enter") {
      e.preventDefault();
      interact(cursor[0], cursor[1]);
    }
  };

  /* ───────────── view bits ───────────── */
  const portraits2d = useMemo(() => Object.fromEntries(TOWER_TYPES.map((t) => [t, towerPortrait(t)])) as Record<TowerType, string>, []);
  // in 3D the pictures come from the very same models that stand on the field (cached by the stage)
  const pic = (t: TowerType, lv = 1) => (mode === "3d" ? stageRef.current?.portrait(t, lv) : "") || portraits2d[t];
  const goldLeft = Math.max(0, b.maxCorrect - b.correctThisRound);
  const upCost = sel && sel.level < 3 ? TOWERS[sel.type].upgrades[sel.level - 1]! : null;
  const selDef = sel ? TOWERS[sel.type] : null;
  const speedLabel = `×${speed}`;
  const chargeCount: number = b.charges ?? 0;
  const streakInSet = (b.streak ?? 0) % (b.boostEvery ?? 3);

  return (
    <div className={`td2 ${boosting ? "boosting" : ""}`}>
      <div className="td2-main">
        <div className="td2-stage">
          <div
            ref={hostRef}
            className={`td2-host ${mode === "2d" ? "d2" : "d3"}`}
            tabIndex={0}
            role="application"
            aria-label={`Tårnforsvar. Runde ${waveNo} af ${b.waves}. ${towers.length} forsvarere, ${lives} liv og ${cash} guld. Brug piletaster og Enter for at bygge.`}
            style={{ cursor: placing ? "copy" : hover && towers.some((t: Tower) => t.x === hover[0] && t.y === hover[1]) ? "pointer" : "default" }}
            onMouseMove={(e: any) => setHover(cellFrom(e))}
            onMouseLeave={() => setHover(null)}
            onClick={(e: any) => {
              if (stageRef.current?.rig.dragged) return;
              const c = cellFrom(e);
              if (!c) { setSelId(null); return; }
              setCursor(c);
              interact(c[0], c[1]);
            }}
            onKeyDown={onCanvasKey}
            onFocus={() => setHover(cursor)}
          >
            {mode === "2d" && <canvas ref={canvasRef} className="td2-canvas" style={{ aspectRatio: `${W} / ${H}` }} />}
            {mode === "loading" && <div className="td2-loading" role="status"><i aria-hidden="true" />Gør banen klar…</div>}
          </div>
          {mode === "3d" && (
            <div className="td2-hud bl td2-cam" role="group" aria-label="Kamera">
              <button className="td2-round-btn rot" onClick={() => stageRef.current?.rig.rotate(-0.26)} aria-label="Drej banen mod uret" title="Drej banen (eller træk med højre musetast)"><LuRotateCcw aria-hidden="true" /></button>
              <button className="td2-round-btn rot" onClick={() => stageRef.current?.rig.rotate(0.26)} aria-label="Drej banen med uret" title="Drej banen"><LuRotateCw aria-hidden="true" /></button>
              <button className="td2-round-btn" onClick={() => stageRef.current?.rig.zoom(0.8)} aria-label="Zoom ind" title="Zoom ind (musehjul)"><LuZoomIn aria-hidden="true" /></button>
              <button className="td2-round-btn" onClick={() => stageRef.current?.rig.zoom(1.25)} aria-label="Zoom ud" title="Zoom ud"><LuZoomOut aria-hidden="true" /></button>
              <button className="td2-round-btn" onClick={() => stageRef.current?.rig.reset()} aria-label="Nulstil kameraet" title="Nulstil kameraet"><LuLocateFixed aria-hidden="true" /></button>
            </div>
          )}
          {help && (
            <div className="td2-help" role="dialog" aria-label="Sådan spiller du" onClick={(e: any) => { if (e.target === e.currentTarget) closeHelp(); }}>
              <div className="card">
                <h3>Sådan spiller du</h3>
                <ol>
                  <li><b>Svar på quizzen</b> under banen. Rigtige svar giver <i className="coin" aria-hidden="true" />guld.</li>
                  <li><b>Køb et forsvar:</b> tryk på det i menuen til højre og derefter på et <b>grønt felt</b> ved vejen (du kan også trække det derud).</li>
                  <li>Tryk på den grønne knap <b>"Start runde"</b>. Så ruller <b>alle rundens balloner</b> ud på banen – forsvarene skyder af sig selv.</li>
                  <li><b>Tryk på et forsvar</b> på banen for at opgradere, vælge hvem det skal ramme eller sælge det.</li>
                  <li><b>Efter runde 15</b> kan du trykke <b>"Spil videre"</b>: så kommer der endeløse runder, som bliver sværere og sværere. Hvor langt når du?</li>
                  <li><b>Flere forsvarere:</b> du starter med 5. Hver gang du stiger et <b>niveau</b> på hjemmesiden (ved at spille de andre spil), åbnes et nyt forsvar – op til 25!</li>
                </ol>
                <p className="more"><b>Kamera:</b> drej med højre museknap eller knapperne nederst til venstre · zoom med Ctrl + musehjul · to fingre på touch.</p>
                <p className="more keys"><b>Taster:</b> Q W E R T vælger forsvar · 1–4 svarer · Mellemrum starter/pauser · F fart · B hjerne-boost · Esc fortryd.</p>
                <p className="more ver" data-testid="td-version">Tårnforsvar v10 · visning: {mode === "3d" ? "3D" : mode === "2d" ? "flad 2D" : "indlæser …"}{reduced ? " · mindre bevægelse (runden vises stadig)" : ""}</p>
                <button className="go" autoFocus onClick={closeHelp}>Forstået – lad os spille!</button>
              </div>
            </div>
          )}
          <div className="td2-gains" aria-hidden="true">{gains.map((g) => <span key={g.id} className={`td2-gain ${g.kind}`}>{g.text}</span>)}</div>
          <div className="td2-hud tl" role="status" aria-live="off">
            <span className="td2-stat hp" title="Liv tilbage"><svg viewBox="0 0 24 24" aria-hidden="true"><path d="M12 21s-8-5.2-8-11a4.6 4.6 0 0 1 8-3 4.6 4.6 0 0 1 8 3c0 5.8-8 11-8 11z" fill="#ef3b3b" stroke="#fff" strokeWidth="1.6" strokeLinejoin="round" /><ellipse cx="8.5" cy="8" rx="2" ry="1.3" fill="#fff" opacity=".7" /></svg><b>{lives}</b></span>
            <span className="td2-stat gold" title="Guld"><svg viewBox="0 0 24 24" aria-hidden="true"><circle cx="12" cy="12" r="9.5" fill="#ffc61a" stroke="#fff" strokeWidth="1.6" /><circle cx="12" cy="12" r="6" fill="none" stroke="#c98a00" strokeWidth="1.6" /></svg><b>{cash}</b></span>
            <span className={`td2-stat flow ${live ? "on" : ""}`} title="Balloner i denne runde" data-testid="td-flow">
              <svg viewBox="0 0 24 24" aria-hidden="true"><ellipse cx="12" cy="10" rx="6.6" ry="7.8" fill="#ff5a6e" stroke="#fff" strokeWidth="1.6" /><ellipse cx="9.6" cy="7.2" rx="1.8" ry="2.6" fill="#fff" opacity=".6" /><path d="M12 18l-1.8 3.2h3.6z" fill="#ff5a6e" stroke="#fff" strokeWidth="1.2" strokeLinejoin="round" /></svg>
              {live && sim ? <span><b>{sim.balloons.length}</b> på banen<em> · {sim.waiting} på vej</em></span> : <span><b>{planned}</b> venter<em> i runde {waveNo}</em></span>}
            </span>
          </div>
          <div className="td2-hud tr">
            <span className={`td2-round ${endless ? "endless" : ""}`} title={endless ? "Endeløse runder – de bliver sværere og sværere" : `${b.mapName} · ${b.difficultyLabel}`}>Runde<b>{waveNo}{endless ? <small><LuInfinity aria-hidden="true" /></small> : <small>/{b.waves}</small>}</b></span>
            <button className="td2-round-btn tog" onClick={() => (help ? closeHelp() : setHelp(true))} aria-pressed={help} aria-label="Sådan spiller du" title="Sådan spiller du"><LuCircleHelp aria-hidden="true" /></button>
            <button className={`td2-round-btn tog ${snd ? "" : "off"}`} onClick={() => { audio.unlock(); audio.setSound(!snd); setSnd(!snd); }} aria-pressed={snd} aria-label={snd ? "Slå lydeffekter fra" : "Slå lydeffekter til"} title={snd ? "Lydeffekter: til" : "Lydeffekter: fra"}>{snd ? <LuVolume2 aria-hidden="true" /> : <LuVolumeX aria-hidden="true" />}</button>
            <button className={`td2-round-btn tog ${mus ? "" : "off"}`} onClick={() => { audio.unlock(); audio.setMusic(!mus); setMus(!mus); }} aria-pressed={mus} aria-label={mus ? "Slå musikken fra" : "Slå musikken til"} title={mus ? "Musik: til" : "Musik: fra"}><LuMusic aria-hidden="true" /></button>
            {mode === "3d" && (
              <button className="td2-round-btn tog gfx" onClick={cycleGfx} aria-label={`Grafik: ${GFX_LABEL[gfx]}. Tryk for at skifte`} title={`Grafik: ${GFX_LABEL[gfx]} (tryk for at skifte)`}><LuSparkles aria-hidden="true" /><small>{GFX_SHORT[gfx]}</small></button>
            )}
            {live && (
              <>
                <button className="td2-round-btn" onClick={() => setPaused((v) => !v)} aria-label={paused ? "Fortsæt" : "Pause"}>{paused ? <LuPlay aria-hidden="true" /> : <LuPause aria-hidden="true" />}</button>
                <button className="td2-round-btn" onClick={() => setSpeed((s) => (s % 3) + 1)} aria-label={`Fart ${speedLabel}. Tryk for at skifte`}><LuFastForward aria-hidden="true" /><small>{speedLabel}</small></button>
              </>
            )}
          </div>
          {startMsg && live && <div className="td2-banner wave" role="status">🎈 {startMsg}</div>}
          {boosting && <div className="td2-banner boost" aria-hidden="true">⚡ HJERNE-BOOST!</div>}
          {paused && live && <div className="td2-banner" aria-live="polite">Pause</div>}
          {!live && !over && roundNote && <div className="td2-banner note" role="status">{roundNote}</div>}
          {!live && !over && !roundNote && b.wave === 0 && towers.length === 0 && <div className="td2-banner tip" role="status">Vælg et forsvar i menuen → og tryk på banen. Svar på quizzen nedenfor for at tjene guld!</div>}
          <div className="td2-mapchip" aria-label={`Bane: ${b.mapName}. Sværhedsgrad: ${b.difficultyLabel}.`}><span className={`dot m-${b.mapId}`} aria-hidden="true" />{b.mapName}<em>{b.difficultyLabel}</em></div>
          {over && <div className={`td2-banner end ${st.won ? "win" : ""}`}>{st.won ? (endless ? `Basen holdt til runde ${b.wave}!` : "Basen holdt!") : "Basen faldt"}</div>}
          {victory && (
            <div className="td2-victory" role="dialog" aria-modal="true" aria-label="Du har klaret alle 15 runder">
              <div className="card">
                <div className="trophy" aria-hidden="true"><LuTrophy /></div>
                <h3>Du klarede alle {b.waves} runder!</h3>
                <p className="lead">{b.mapName} · {b.difficultyLabel} · {b.pops} lag punkteret · {b.lives} liv tilbage</p>
                <p>Basen holder. Vil du stoppe som vinder – eller <b>spille videre</b>? Rundene efter runde {b.waves} bliver <b>sværere og sværere</b>: flere balloner, tungere bly, hurtigere bosser. Se hvor langt du kan nå!</p>
                <div className="choices">
                  <button className="go" autoFocus onClick={() => choose("continue")}><LuInfinity aria-hidden="true" /> Spil videre<small>+{b.continueBonus} guld til at forberede runde {b.waves + 1}</small></button>
                  <button className="stop" onClick={() => choose("finish")}><LuFlag aria-hidden="true" /> Afslut og hent belønning</button>
                </div>
              </div>
            </div>
          )}
          {msg && <div className="td2-toast" role="alert">{msg}</div>}
          {placing && !over && <div className="td2-hint" role="status"><b>{TOWERS[placing].name}</b> – tryk på et frit grønt felt. <button className="linklike" onClick={() => setPlacing(null)}>Fortryd (Esc)</button></div>}
        </div>

        <QuizStrip
          q={q}
          now={st.now}
          banner={banner}
          answering={answering}
          onAnswer={submitAnswer}
          streak={b.streak ?? 0}
          streakInSet={streakInSet}
          boostEvery={b.boostEvery ?? 3}
          goldLeft={goldLeft}
          goldPerAnswer={b.goldPerAnswer}
          streakBonus={b.streakBonus ?? 0}
          over={over}
          victory={victory}
        />
      </div>

      <aside className="td2-side" aria-label="Forsvarere">
        {sel && selDef && !over ? (
          <div className="td2-panel" role="region" aria-label={`${selDef.name}, niveau ${sel.level}`}>
            <div className="td2-ptitle"><button className="td2-back" onClick={() => setSelId(null)} aria-label="Tilbage til butikken">‹</button> {selDef.name}</div>
            <div className="td2-pcard">
              <img src={pic(sel.type, sel.level)} alt="" width={72} height={72} />
              <div>
                <b>Niveau {sel.level}/3</b>
                <p>{selDef.levels[sel.level - 1]!.note}{selDef.levels[sel.level - 1]!.hitsLead || selDef.levels[sel.level - 1]!.income || selDef.levels[sel.level - 1]!.buff ? "" : " · kan ikke ramme bly"}</p>
              </div>
            </div>
            <dl className="td2-stats" aria-label="Egenskaber">
              {towerStats(selDef, sel.level).map(([k2, v]) => {
                const nx = sel.level < 3 ? towerStats(selDef, sel.level + 1).find(([kk]) => kk === k2)?.[1] : undefined;
                return <Fragment key={k2}><dt>{k2}</dt><dd>{v}{nx && nx !== v ? <em className="next"> → {nx}</em> : null}</dd></Fragment>;
              })}
            </dl>
            {upCost !== null ? (
              <button className={`td2-up ${cash >= upCost ? "" : "poor"}`} onClick={() => doAct({ type: "upgrade", id: sel.id })} disabled={cash < upCost}>
                <span>Opgradér</span><small>{selDef.levels[sel.level]!.note}</small><em>{upCost}</em>
              </button>
            ) : <div className="td2-max">★ Højeste niveau</div>}
            {!selDef.levels[sel.level - 1]!.income && !selDef.levels[sel.level - 1]!.buff && <div className="td2-target" role="group" aria-label="Sigt efter">
              <small>Sigt efter</small>
              <div className="segment">
                {(b.targetModes as TargetMode[]).map((m) => <button type="button" key={m} aria-pressed={sel.target === m} onClick={() => doAct({ type: "target", id: sel.id, mode: m })}>{TARGET_LABEL[m]}</button>)}
              </div>
            </div>}
            <button className="td2-sell" onClick={() => { doAct({ type: "sell", id: sel.id }); setSelId(null); }}>Sælg for {towerSellValue(sel)} guld</button>
          </div>
        ) : (
          <div className="td2-panel" role="group" aria-label="Køb forsvarere (taster Q W E R T for de første fem)">
            <div className="td2-ptitle">Forsvarere <small data-testid="td-unlocked">{openCount}/{TOWER_TYPES.length} åbnet</small></div>
            <div className="td2-shopwrap" data-testid="td-shop">
              <div className="td2-shop">
                {TOWER_TYPES.map((k, i) => {
                  const d = TOWERS[k];
                  if (d.unlock > lvlNow) {
                    return (
                      <div key={k} className={`td2-card locked ${d.unlock === nextUnlock ? "next" : ""}`} data-tower={k} tabIndex={0} role="img" aria-label={`${d.name} er låst. Åbner på niveau ${d.unlock}.`} onClick={() => say(lockedMsg(k))} onMouseEnter={(e: any) => showTip(k, e.currentTarget)} onFocus={(e: any) => showTip(k, e.currentTarget)} onMouseLeave={() => setTip(null)} onBlur={() => setTip(null)} style={{ ["--tc" as any]: d.color }}>
                        <span className="ph" aria-hidden="true">{d.icon}</span>
                        <span className="nm">{d.name}</span>
                        <span className="lk"><LuLock aria-hidden="true" /> Niveau {d.unlock}</span>
                      </div>
                    );
                  }
                  const afford = cash >= d.cost;
                  const ready = i < picsReady;
                  return (
                    <button key={k} className={`td2-card ${placing === k ? "on" : ""} ${afford ? "" : "poor"}`} data-tower={k} aria-pressed={placing === k} aria-label={`${d.name}, ${d.cost} guld. ${d.blurb}`} onClick={() => pick(k)} onPointerDown={(e: any) => onCardDown(k, e)} onMouseEnter={(e: any) => showTip(k, e.currentTarget)} onFocus={(e: any) => showTip(k, e.currentTarget)} onMouseLeave={() => setTip(null)} onBlur={() => setTip(null)} style={{ ["--tc" as any]: d.color }}>
                      {ready ? <img src={pic(k, 1)} alt="" width={56} height={56} draggable={false} /> : <span className="ph" aria-hidden="true">{d.icon}</span>}
                      <span className="nm">{d.name}</span>
                      <span className="pr"><i aria-hidden="true" />{d.cost}</span>
                      {i < TOWER_KEYS.length && <kbd aria-hidden="true">{TOWER_KEYS[i]!.toUpperCase()}</kbd>}
                    </button>
                  );
                })}
              </div>
            </div>
            <div className="td2-shopfoot">
              <span className="cnt" title="Forsvar på banen"><b>{towers.length}/{b.maxTowers}</b> på banen</span>
              {nextUnlock !== null && <span className="nxt"><LuLock aria-hidden="true" /> Næste: niveau {nextUnlock}</span>}
            </div>
            <p className="td2-blurb" role="status">{placing ? <><b>{TOWERS[placing].name}:</b> {TOWERS[placing].blurb}</> : "Tryk på et forsvar – eller træk det ud på banen. Stig i niveau på hjemmesiden for at åbne flere!"}</p>
          </div>
        )}

        <div className="td2-actions">
          <button className={`td2-boost ${chargeCount > 0 && live ? "ready" : ""}`} onClick={() => doAct({ type: "boost" })} disabled={over || !live || chargeCount <= 0} aria-label={`Hjerne-boost: ${chargeCount} klar. Gør alle forsvarere dobbelt så hurtige i 8 sekunder.`} title="Svar rigtigt tre gange i træk for at lade op. Gør alle forsvarere dobbelt så hurtige i 8 sekunder (B).">
            <LuZap aria-hidden="true" />
            <span><b>Hjerne-boost</b><small>{chargeCount > 0 ? `${chargeCount} klar – tryk B` : `${streakInSet}/${b.boostEvery ?? 3} rigtige i træk`}</small></span>
            <i className="charges" aria-hidden="true">{Array.from({ length: b.maxCharges ?? 3 }, (_, i) => <u key={i} className={i < chargeCount ? "on" : ""} />)}</i>
          </button>
          {!over && !victory && (
            <button className={`td2-go ${live ? "ff" : ""}`} disabled={!live && towers.length === 0} onClick={() => (live ? setSpeed((s) => (s % 3) + 1) : startRound())} aria-label={live ? `Fart ${speedLabel}` : `Start runde ${waveNo}`}>
              {live ? <><LuFastForward aria-hidden="true" /> Fart {speedLabel}</> : <><LuPlay aria-hidden="true" /> Start runde {waveNo}</>}
            </button>
          )}
          {!over && !victory && !live && <small className="td2-next">{endless ? "Endeløs runde" : "Næste runde"}: {b.nextCount} balloner{b.nextInfo?.boss ? " · BOSS" : ""} · bonus +{b.nextBonus} guld</small>}
          {endless && !over && !live && (
            confirmEnd ? (
              <div className="td2-endrow" role="group" aria-label="Afslut spillet?">
                <button className="td2-finish yes" onClick={() => choose("finish")}>Ja, afslut</button>
                <button className="td2-finish no" onClick={() => setConfirmEnd(false)}>Spil videre</button>
              </div>
            ) : <button className="td2-finish" onClick={() => setConfirmEnd(true)}><LuFlag aria-hidden="true" /> Afslut spillet (du beholder sejren)</button>
          )}
          {!over && <label className="td2-auto"><input type="checkbox" checked={auto} onChange={(e: any) => setAuto(e.target.checked)} /> Start næste runde automatisk</label>}
        </div>
      </aside>
      {tip && !placing && (
        <div className={`td2-tip side ${tip.above ? "above" : ""}`} role="tooltip" style={tip.above ? { left: tip.left, bottom: tip.bottom, ["--tc" as any]: TOWERS[tip.type].color } : { right: tip.right, top: tip.top, ["--tc" as any]: TOWERS[tip.type].color }}>
          <div className="th"><b>{TOWERS[tip.type].name}</b><span className="pr"><i aria-hidden="true" />{TOWERS[tip.type].cost}</span></div>
          <p>{TOWERS[tip.type].blurb}</p>
          {TOWERS[tip.type].unlock > lvlNow && <div className="lock"><LuLock aria-hidden="true" /> Låses op på niveau {TOWERS[tip.type].unlock} – du er på niveau {lvlNow}. Spil flere spil på hjemmesiden!</div>}
          <dl>{towerStats(TOWERS[tip.type], 1).map(([k2, v]) => <Fragment key={k2}><dt>{k2}</dt><dd>{v}</dd></Fragment>)}</dl>
          <div className="up">Opgraderes for {TOWERS[tip.type].upgrades[0]} og {TOWERS[tip.type].upgrades[1]} guld</div>
        </div>
      )}
    </div>
  );
}

/* ───────────── the quiz strip under the map ───────────── */
function QuizStrip(props: {
  q: any; now?: number; banner: any; answering: boolean; onAnswer: (g: number | string) => void;
  streak: number; streakInSet: number; boostEvery: number; goldLeft: number; goldPerAnswer: number[]; streakBonus: number; over: boolean; victory?: boolean;
}) {
  const { q, banner } = props;
  const left = useCountdown(q?.deadline, props.now);
  const total = q ? Math.max(1, (q.deadline ?? 0) - (q.issuedAt ?? 0)) : 1;
  const [text, setText] = useState("");
  useEffect(() => setText(""), [q?.pid]);
  const worth = q ? (props.goldLeft > 0 ? (props.goldPerAnswer[q.difficulty - 1] ?? 0) + props.streakBonus : 0) : 0;
  if (props.over) return <section className="td2-quiz done" aria-label="Quiz"><p>Spillet er slut. Godt klaret!</p></section>;
  if (props.victory) return <section className="td2-quiz done" aria-label="Quiz"><p>Fantastisk – alle runder er klaret! Vælg, om du vil spille videre.</p></section>;
  return (
    <section className="td2-quiz" aria-label="Quiz – svar og tjen guld" aria-live="polite">
      <div className="td2-streak" title="Rigtige svar i træk. Hver 3. lader Hjerne-boost op.">
        <span className="flame" aria-hidden="true">🔥</span>
        <b>{props.streak}</b>
        <i aria-hidden="true">{Array.from({ length: props.boostEvery }, (_, i) => <u key={i} className={i < props.streakInSet ? "on" : ""} />)}</i>
      </div>
      <div className="td2-qmain">
        {banner ? (
          <div className={`td2-fb ${banner.correct ? "good" : "bad"}`} role="status">
            <b>{banner.correct ? "✓ Rigtigt!" : banner.timedOut ? "⏱ Tiden løb ud" : "✗ Næsten!"}</b>
            {banner.correct && banner.reward ? <span>{banner.reward.gold > 0 ? ` +${banner.reward.gold} guld` : " (guldgrænsen for runden er nået)"}{banner.reward.charge ? " · ⚡ Hjerne-boost klar!" : banner.reward.streak > 1 ? ` · ${banner.reward.streak} i træk` : ""}</span> : null}
            {!banner.correct && <span> Svaret er: <strong>{banner.correctText}</strong>. {banner.explanation}</span>}
          </div>
        ) : q ? (
          <>
            <div className="td2-qhead">
              <span className="chip subj">{subjectName(q.subject)}</span>
              <span className={`chip worth ${worth > 0 ? "" : "none"}`}>{worth > 0 ? `+${worth} guld` : "ingen guld mere i denne runde – men boost!"}</span>
              <span className="chip time" aria-label={`${Math.ceil(left / 1000)} sekunder tilbage`}>{Math.ceil(left / 1000)} s</span>
            </div>
            <div className="td2-qtext" id={`tq-${q.pid}`}>{q.text}</div>
            {q.kind === "short" ? (
              <form className="td2-short" onSubmit={(e: any) => { e.preventDefault(); if (text.trim() && !props.answering) props.onAnswer(text.trim()); }}>
                <input value={text} onChange={(e: any) => setText(e.target.value)} placeholder="Skriv dit svar" aria-labelledby={`tq-${q.pid}`} maxLength={100} autoComplete="off" disabled={props.answering} />
                <button className="btn" disabled={props.answering || !text.trim()}>Svar</button>
              </form>
            ) : (
              <div className="td2-answers" role="group" aria-labelledby={`tq-${q.pid}`}>
                {q.options.map((o: string, i: number) => (
                  <button key={i} className="td2-ans" style={{ ["--ac" as any]: ANS_COLORS[i % 4] }} disabled={props.answering} onClick={() => props.onAnswer(i)}>
                    <span className="k" aria-hidden="true">{"ABCD"[i]}</span><span className="t">{o}</span><kbd aria-hidden="true">{i + 1}</kbd>
                  </button>
                ))}
              </div>
            )}
            <div className="td2-qtimer" aria-hidden="true"><i style={{ transform: `scaleX(${Math.max(0, Math.min(1, left / total))})` }} /></div>
          </>
        ) : (
          <p className="td2-wait">Henter næste spørgsmål…</p>
        )}
      </div>
    </section>
  );
}

void LAYER_COLORS;
void WAVES;
